/**
 * VendorPortalPaymentService
 * Service for managing vendor payment configurations (CRUD).
 * Route: api/vendor-portal/payments
 *
 * Vendors can:
 * - View all their payment configurations
 * - Create new payment methods
 * - Update existing payment methods
 * - Delete payment methods
 * - View payment summary
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalPaymentService
 * Handles vendor payment configuration management for the vendor portal.
 * Maps to /api/vendor-portal/payments endpoint
 */
export class VendorPortalPaymentService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalPaymentService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalPaymentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all payments for the current vendor
     * GET /api/vendor-portal/payments
     */
    async getPayments() {
        const response = await this.getPaymentsRaw();
        return response.data ?? [];
    }
    /**
     * Get all payments (raw response)
     */
    getPaymentsRaw() {
        return this.get();
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific payment by ID
     * GET /api/vendor-portal/payments/{id}
     */
    async getPayment(id) {
        const response = await this.getPaymentRaw(id);
        if (!response.data) {
            throw new Error(`Payment with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific payment (raw response)
     */
    getPaymentRaw(id) {
        return this.get(String(id));
    }
    // ==================== CREATE ====================
    /**
     * Create a new payment configuration for the current vendor
     * POST /api/vendor-portal/payments
     */
    async createPayment(request) {
        const response = await this.createPaymentRaw(request);
        if (!response.data) {
            throw new Error('Failed to create payment configuration');
        }
        return response.data;
    }
    /**
     * Create a new payment configuration (raw response)
     */
    createPaymentRaw(request) {
        return this.post('', request);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing payment configuration
     * PUT /api/vendor-portal/payments/{id}
     * Server validates ownership before updating
     */
    async updatePayment(id, request) {
        const response = await this.updatePaymentRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to update payment configuration ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing payment configuration (raw response)
     */
    updatePaymentRaw(id, request) {
        return this.put(String(id), request);
    }
    // ==================== DELETE ====================
    /**
     * Delete a payment configuration
     * DELETE /api/vendor-portal/payments/{id}
     * Server validates ownership before deleting
     *
     * Note: At least one active configuration is recommended but not strictly required
     */
    async deletePayment(id) {
        await this.deletePaymentRaw(id);
    }
    /**
     * Delete a payment configuration (raw response)
     */
    deletePaymentRaw(id) {
        return this.delete(String(id));
    }
    // ==================== SUMMARY ====================
    /**
     * Get payment summary for the current vendor
     * GET /api/vendor-portal/payments/summary
     */
    async getSummary() {
        const response = await this.getSummaryRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve payment summary');
        }
        return response.data;
    }
    /**
     * Get payment summary (raw response)
     */
    getSummaryRaw() {
        return this.get('summary');
    }
}
VendorPortalPaymentService.DEFAULT_SERVICE_PATH = 'vendors';
VendorPortalPaymentService.BASE_PATH = 'api/vendor-portal/payments';
//# sourceMappingURL=VendorPortalPaymentService.js.map